import { parseHotkey } from "is-hotkey";
import { Editor, Range, Transforms } from "slate";
import { useSlate } from "slate-react";

import {
  EditorElement,
  isEditorElementType,
} from "@smart/itops-serialisation-basic";

import { EditorPlaceholders, useEditorContext } from "./context";
import { IconName } from "../../icon";

type InsertPlaceholderOptions = {
  name: string;
  displayText?: string;
  casing?: "lowercase";
};

export const insertPlaceholder = (
  editor: Editor,
  placeholders: EditorPlaceholders,
  options: InsertPlaceholderOptions,
  element?: EditorElement,
) => {
  const { name, displayText, casing } = options;
  const details =
    placeholders.details[name as keyof typeof placeholders.details];
  if (!details) return;

  const updated: EditorElement<"placeholder"> = {
    type: "placeholder",
    name,
    isLink: details.isLink,
    displayText: details.isLink
      ? (displayText ?? details.defaultValue ?? details.label)
      : details.label,
    casing: casing === "lowercase" ? "lowercase" : undefined,
    children: [{ text: "" }],
  };

  if (element && isEditorElementType("placeholder")(element)) {
    Transforms.setNodes(editor, updated, {
      match: (n) => n === element,
    });
  } else {
    if (editor.selection && Range.isExpanded(editor.selection)) {
      Transforms.delete(editor, {
        at: editor.selection,
      });
    }
    Transforms.insertNodes(editor, updated);
  }
};

export const useBuildPlaceholderMenu = () => {
  const { placeholders } = useEditorContext();
  const editor = useSlate();

  if (!placeholders?.keys.length) return undefined;

  return {
    id: "placeholder",
    icon: "regularBracketsCurly" as IconName,
    pressedIcon: "solidBracketsCurly" as IconName,
    hotkey: parseHotkey("mod+shift+c", { byKey: true }),
    text: "Insert Field",
    items: placeholders?.keys.map((key) => ({
      label:
        placeholders.details[key as keyof typeof placeholders.details].label,
      onClick: () => {
        insertPlaceholder(editor, placeholders, { name: key });
      },
      subMenu:
        key === "matterType"
          ? [
              {
                label: "Title Case",
                onClick: () => {
                  insertPlaceholder(editor, placeholders, { name: key });
                },
              },
              {
                label: "Lowercase",
                onClick: () => {
                  insertPlaceholder(editor, placeholders, {
                    name: key,
                    casing: "lowercase",
                  });
                },
              },
            ]
          : undefined,
    })),
  };
};
